import {
  whichBuild, apiStore, apiGet, apiPost, generateFormData,
} from '../../modules/apiHelper';
import { now, isStaleData, objectIsValid } from '../../modules/dataHelper';
import resolve from '../../modules/api/resolve';

/**
 * Allowed list of companies, sort of allowed-list
 */
const knownCompanyKeys = ['ementored', 'kengen', 'fellowship', 'wema', 'slap', 'huawei', 'any'];

function normalizeHost(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split(':')[0];
}

function normalizeCompanyKey(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9-]/g, '');
}

function getSubdomainKey(host = '') {
  const parts = normalizeHost(host).split('.').filter(Boolean);
  if (parts.length < 3) return '';

  const key = normalizeCompanyKey(parts[0]);
  if (key === 'app') return 'ementored';
  if (key === 'www') return '';

  return key;
}

function resolveCompanyKey(scope = {}, currentHost = '') {
  const candidates = [
    scope.slug,
    scope.name,
    scope.company_name,
    scope.shortname,
    getSubdomainKey(scope.website),
    getSubdomainKey(currentHost),
  ]
    .map(normalizeCompanyKey)
    .filter(Boolean);

  const match = candidates.find((key) => knownCompanyKeys.includes(key));
  return match || 'any';
}

export default {
  state: {
    scopeId: 0,
    companyKey: 'any',
    programType: undefined,
    programTypes: {
      goalOriented: 1,
      sequential: 2,
      selfMentorship: 3
    },
    programStatus: undefined,
    programStatuses: {
      inactive: 0,
      active: 1,
      deactivated: 2,
      ended: 3, // also means 'archived'
    },
  },
  getters: {
    scopeId(state) {
      return state.scopeId;
    },
    companyKey(state) {
      return state.companyKey;
    },
    isEmentored(state) {
      return state.companyKey === 'ementored';
    },
    isKengen(state) {
      return state.companyKey === 'kengen';
    },
    isFellowship(state) {
      return state.companyKey === 'fellowship';
    },
    isWema(state) {
      return state.companyKey === 'wema';
    },
    isSlap(state) {
      return state.companyKey === 'slap';
    },
    isGoalOrientedProgram(state) {
      if (!state.programType) return true;
      return state.programType === state.programTypes.goalOriented;
    },
    isSequentialProgram(state) {
      if (!state.programType) return false;
      return state.programType === state.programTypes.sequential;
    },
    isSelfMentorshipProgram(state) {
      if (!state.programType) return false;
      return state.programType === state.programTypes.selfMentorship;
    },
    isArchivedProgram(state) {
      if (!state.programStatus) return false;
      return state.programStatus === state.programStatuses.ended;
    }
  },
  mutations: {
    setScope(state, id) {
      if (!id) return;
      state.scopeId = id;
    },
    setCompanyKey(state, companyKey) {
      state.companyKey = normalizeCompanyKey(companyKey) || 'any';
    },
    setProgramType(state, programType) {
      if (!programType) return;
      state.programType = Number(programType);
    },
    setProgramStatus(state, status) {
      if (!status) return;
      state.programStatus = Number(status);
    }
  },
  actions: {
    /**
     * Returns information of the set scope
     * @param {Object} context $store context
     * @returns {Array} *
     * @ {Boolean} isValidScope if url is a valid scope whether it's the main app or a company url (domain url)
     * @ {Boolean} isMainApp if url is ementored.com
     * @ {Boolean} isDevBuild checks if url is a developer scope
     * @ {Number} scopeId
     *
     * *
     */
    async evaluateScope(context) {
      await context.dispatch('setScope'); // first set scope by guessing, using the url

      if (process.env.NODE_ENV === 'development') {
        return [true, false, true, context.state.scopeId];
      }
      if (whichBuild(false)) return [true, true, true, context.state.scopeId];


      // * evaluates the current url
      const curr = window.location.host; // app.ementored.com

      const mainAppRegexTest = new RegExp(process.env.VUE_APP_MAIN_APP_URL);
      const isMainApp = mainAppRegexTest.test(curr);

      const scopeRegexTest = new RegExp(process.env.VUE_APP_SCOPE_URL);
      const isValidScopeByHost = scopeRegexTest.test(curr);
      const isKnownScope = Boolean(context.getters.scopeId);
      const isValidScope = isValidScopeByHost && (isMainApp || isKnownScope);

      if (isValidScopeByHost && !isMainApp && !isKnownScope) {
        return [false, false, false, 0];
      }

      if (isValidScope && context.rootState.user.companyid) {
        context.commit('setScope', context.rootGetters.userCompanyId);
      }

      return [isValidScope, isMainApp, false, context.getters.scopeId];
      // return [true, false, false];
    },

    // --------------------------------------------------------------------

    // more like guess the scope of the app
    async setScope(context) {
      const curr = normalizeHost(window.location.host);

      await context.dispatch('getCompanies').then((scopes) => {
        if (!Array.isArray(scopes)) return;
        let scope;

        if (process.env.NODE_ENV === 'development' || whichBuild(false)) {
          scope = scopes.find(({ id }) => Number(id) === Number(process.env.VUE_APP_DEFAULT_COMPANY_ID));
        } else {
          // find by the website url
          scope = scopes.find(({ website }) => normalizeHost(website) === curr);
        }

        context.commit('setScope', (scope || {}).id);
        context.commit('setCompanyKey', resolveCompanyKey(scope, curr));
      });
    },

    // --------------------------------------------------------------------

    async getCompanies() {
      const request = resolve(apiGet('company', 6, null, true));

      // wait for the xhr
      const result = await request;
      return (result || {}).company || false;
    },

    // --------------------------------------------------------------------

    async getCompany(context, [CompanyId, refresh = false]) {
      const companyId = CompanyId
        || context.rootGetters.userCompanyId
        || context.getters.scopeId
        || (process.env.NODE_ENV === 'development' ? process.env.VUE_APP_DEFAULT_COMPANY_ID : undefined);
      //
      if (Number.isNaN(Number(companyId))) return false;

      // eslint-disable-next-line no-underscore-dangle
      const _company = await apiStore.getItem(`company/${companyId}`) || [0, {}];

      if (!refresh && !isStaleData(_company[0]) && objectIsValid(_company[1])) return _company[1];

      const companies = await context.dispatch('getCompanies', refresh);

      const company = Array.isArray(companies)
        ? companies.find(({ id }) => Number(companyId) === Number(id)) || false
        : false;

      if (!company) return false;

      await apiStore.setItem(`company/${companyId}`, [now(), company]);

      return company;
    },

    // --------------------------------------------------------------------

    async getProgram(context, [ProgramId, refresh = false]) {
      const programId = ProgramId || context.rootGetters.userProgramId;

      if (Number.isNaN(Number(programId))) return false;

      const cachedProgram = await apiStore.getItem(`program/${programId}`) || [0, {}];

      if (!refresh && !isStaleData(cachedProgram[0]) && objectIsValid(cachedProgram[1])) {
        context.commit('setProgramType', cachedProgram[1].type);
        context.commit('setProgramStatus', cachedProgram[1].status);

        return cachedProgram[1];
      }

      const program = await resolve(apiPost('prog', generateFormData({ progid: programId }), 63));
      const theProgram = program?.prog[0];

      if (!theProgram) return false;

      await apiStore.setItem(`program/${programId}`, [now(), theProgram]);

      context.commit('setProgramType', theProgram.type);
      context.commit('setProgramStatus', theProgram.status);

      return theProgram;
    },

    async getProgramByUuid(context, [ProgramUuid, refresh = false]) {
      if (!ProgramUuid) return false;

      const cachedProgram = await apiStore.getItem(`program/u/${ProgramUuid}`) || [0, {}];

      if (!refresh && !isStaleData(cachedProgram[0]) && objectIsValid(cachedProgram[1])) {
        context.commit('setProgramType', cachedProgram[1].type);
        return cachedProgram[1];
      }

      const program = await resolve(apiPost('prog_u', generateFormData({ u: ProgramUuid }), 63));
      const theProgram = program?.prog_u[0];

      if (!theProgram) return false;

      await apiStore.setItem(`program/u/${ProgramUuid}`, [now(), theProgram]);

      return theProgram;
    },
  },
};
